import { useEffect, useRef, useState } from 'react';

import useSelectPlayerModalAdapter from 'application/components/common/SelectPlayerFlow/SelectPlayerModal/useSelectPlayerModalViewModel';
import { useAccountContext } from 'application/providers/AccountProvider';
import {
  usePointRepositoryInjector,
  useBillingRepositoryInjector,
  useAccountRepositoryInjector,
  usePaymentMethodRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import {
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  NotificationContextType,
} from 'application/providers/NotificationProvider';
import { TEXTS } from 'application/pages/texts';
import { TEXTS as POINT_TEXTS } from 'application/pages/PointsPage/renderer/texts';
import LicensesUtils from 'application/repositories/billing/utils';
import useAddressState from 'application/hooks/useAddressState';
import ValidationUtils from 'application/utils/ValidationUtils';
import AnalyticsService, {
  EventNameGTM,
  ParamNameAMP,
  PointEventNameAMP,
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { PointWithDevice } from 'application/presenters/point/types';

import {
  AcceptanceAdvanceStatus,
  PaymentKind,
  PointInfoTypeFragment,
  UserFeatures,
} from 'domain/api/graphql/generated';
import useAcceptanceAdvanced from 'domain/billing/useAcceptanceAdvanced';

import { PointCreateModalPages, UsePointCreateModalViewModel } from './types';

const POLLING_INTERVAL = 10000;
const STOP_POLLING_TIMEOUT = 300000;

interface Props {
  flowType?: SelectPlayerFlowType;
  isTemplateMode?: boolean;
  targetStreamId?: string;
  collectionId?: string;
  page: PointCreateModalPages;
  notification: NotificationContextType;
  redirectToPointDrawer: (point: PointInfoTypeFragment) => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'open' | 'close' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  openModal: ((type: ModalTypes, props: {}) => void) &
    (<T extends ModalTypes>(
      type: T,
      modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
      onModalCloseCallback?: () => void
    ) => void);
}

const usePointCreateViewModel = ({
  flowType,
  isTemplateMode,
  targetStreamId,
  collectionId,
  page,
  notification,
  redirectToPointDrawer,
  replaceModal,
  openModal,
}: Props): UsePointCreateModalViewModel => {
  const stopPullingTimer = useRef<ReturnType<typeof setTimeout>>();
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const { currentCompany } = useAccountContext();

  const { userOnboardingFeaturesLoading } = useAccountRepositoryInjector();
  const { getPaymentMethod, readPaymentMethod } = usePaymentMethodRepositoryInjector();

  const { paymentMethod } = readPaymentMethod(currentCompany!.id);

  const {
    createPoint,
    createPointLoading,
    createPointError,
    getStandardizedPoints,
    getStandardizedPointsLoading,
    setCurrentPoint,
    readCurrentPoint,
  } = usePointRepositoryInjector();

  const {
    getExtendSubscriptionCost,
    subscriptionCostResult,
    extendOneShotSubscription,
    isExtendSubscriptionLoading,
    getBillingInfo,
    watchBillingInfo,
    licenseCostLoading,
  } = useBillingRepositoryInjector();

  const { hasTrialLicenses, hasActiveLicenses, selectedPaymentKind } = watchBillingInfo(currentCompany!.id);

  const {
    changeStream,
    isChangeStreamLoading,
    copyTemplateStream,
    isCopyStreamTemplateLoading,
    onboardingPassedRequest,
    isUserFeaturesOnboardingPassLoading,
    getSavedStreams,
    isSavedStreamsLoading,
  } = useSelectPlayerModalAdapter();

  const {
    acceptanceAdvanced,
    loading: acceptanceAdvancedLoading,
    startPolling,
    stopPolling,
  } = useAcceptanceAdvanced(currentCompany!.id);

  const addressState = useAddressState({ location: currentCompany?.country });

  const isButtonLoading =
    getStandardizedPointsLoading ||
    createPointLoading ||
    isExtendSubscriptionLoading ||
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    isSavedStreamsLoading ||
    acceptanceAdvancedLoading;

  const isButtonWithCostLoading =
    isButtonLoading || licenseCostLoading || subscriptionCostResult.data.extendCost === undefined;

  const isAcceptanceAdvancedNull =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced === null || acceptanceAdvanced?.status === null) &&
    !hasTrialLicenses;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending;

  const needShowAcceptanceAdvancedModal =
    selectedPaymentKind === PaymentKind.Sbbol && isAcceptanceAdvancedNull && !hasTrialLicenses;

  const isLastInvoiceHasZeroAmount =
    LicensesUtils.getLastPaidPeriodicInvoice(currentCompany?.invoices || [])?.amount === 0;

  const isAcceptanceAdvancedAuthorizedOrActive =
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Authorized ||
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Active;

  const canPay = selectedPaymentKind === PaymentKind.Sbbol ? isAcceptanceAdvancedAuthorizedOrActive : paymentMethod;

  const canAddPointWithPayment =
    currentCompany?.isBillingEnabled && canPay && !isLastInvoiceHasZeroAmount && hasActiveLicenses;

  const startPullingTimeout = () => {
    startPolling(POLLING_INTERVAL);
    setIsPolling(true);

    stopPullingTimer.current = setTimeout(() => {
      stopPolling();
      setIsPolling(false);

      notification.showErrorNotification({
        children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_TOO_LONG,
        delay: 10000,
      });
    }, STOP_POLLING_TIMEOUT);
  };

  const stopProcess = () => {
    notification.hideNotification();
    stopPolling();
    clearTimeout(stopPullingTimer.current);
    setIsPolling(false);

    notification.showInfoNotification({
      children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_ACTIVE,
      delay: 10000,
    });
  };

  useEffect(() => {
    switch (acceptanceAdvanced?.status) {
      case AcceptanceAdvanceStatus.Pending: {
        if (!stopPullingTimer.current) {
          startPullingTimeout();

          notification.showInfoNotification({
            children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_PENDING,
            delay: 10000,
          });
        }

        break;
      }
      case AcceptanceAdvanceStatus.Canceled: {
        notification.showInfoNotification({
          children: TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_CANCELLED,
          delay: 10000,
        });

        stopPolling();
        clearTimeout(stopPullingTimer.current);
        setIsPolling(false);
        break;
      }
      case AcceptanceAdvanceStatus.Authorized: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case AcceptanceAdvanceStatus.Active: {
        if (isPolling) {
          stopProcess();
        }

        break;
      }
      case null: {
        stopPolling();
        clearTimeout(stopPullingTimer.current);
        setIsPolling(false);

        break;
      }
      default: {
        break;
      }
    }
  }, [acceptanceAdvanced?.status, isPolling]);

  const initData = async () => {
    if (currentCompany) {
      await getBillingInfo(currentCompany.id);
      await getPaymentMethod(currentCompany.id);
    }

    if (currentCompany?.isBillingEnabled) {
      await getExtendSubscriptionCost(currentCompany?.id!, 1);
    }
  };

  useEffect(() => {
    initData();

    return () => {
      if (stopPullingTimer.current) {
        clearTimeout(stopPullingTimer.current);
      }
    };
  }, []);

  const changeStreamOnNewPoint = async () => {
    const currentPoint = readCurrentPoint();

    let streamId = targetStreamId;

    if (isTemplateMode) {
      streamId = (await copyTemplateStream(streamId!, currentCompany!.id))!;
    }

    changeStream([
      {
        newStreamId: streamId!,
        pointId: currentPoint!.id,
      },
    ]);

    getSavedStreams([currentCompany!.id]);
    onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    const stream = { id: streamId };
    const newPoint = { ...currentPoint, stream } as PointWithDevice;
    setCurrentPoint(newPoint);

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
      [ParamNameAMP.STREAM_ID]: streamId,
      [ParamNameAMP.COLLECTION_ID]: collectionId,
    });

    return newPoint;
  };

  const onCreatePointModalNext = async () => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_RUN_AT_CREATED_POINT_CLICKED, {
      [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
    });

    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_MODAL_SUCCESS_SUBMIT, {
      [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
    });

    replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
  };

  const handleSuccessCreatePointWithPayment = async () => {
    const currentPoint = readCurrentPoint();

    try {
      if (canAddPointWithPayment) {
        await extendOneShotSubscription(currentCompany?.id!, [currentPoint!.license!.id]);

        notification.showInfoNotification({
          children: POINT_TEXTS.POINT_CREATE_CONFIRM_MODAL_WITH_PAYMENT_SUCCESS_TEXT,
        });
      }
    } catch (e) {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    } finally {
      let point = { ...currentPoint } as PointInfoTypeFragment;

      if (page === PointCreateModalPages.STREAM) {
        point = await changeStreamOnNewPoint();
      }

      if (page === PointCreateModalPages.POINT) {
        redirectToPointDrawer(point);
      }

      onCreatePointModalNext();
    }
  };

  const handleCreateClick = async () => {
    const defaultTimezone = 'Europe/Moscow';
    const requestData = await getStandardizedPoints(addressState.value);

    const standardizedPoint = requestData?.length > 0 ? requestData[0] : null;
    const timezone = requestData ? standardizedPoint?.timezone : defaultTimezone;

    const addressData = standardizedPoint && {
      fiasId: standardizedPoint.fiasId,
      street: standardizedPoint.street,
      country: standardizedPoint.country,
      city: standardizedPoint.city,
      region: standardizedPoint.region,
      postalCode: standardizedPoint.postalCode,
      latitude: Number(standardizedPoint.latitude),
      longitude: Number(standardizedPoint.longitude),
      fullAddress: standardizedPoint.address,
    };

    const result = await createPoint({
      companyId: currentCompany!.id,
      address: addressState.value,
      timezone,
      createWebDevice: false,
      addressData,
    });

    AnalyticsService.event(PointEventNameAMP.POINT_CREATED, {
      [PointParamNameAMP.POINT_ID]: result.point?.id,
      [ParamNameAMP.CALLED_FROM]: page,
      [PointParamNameAMP.POINT_IS_ENABLED]: result.point?.isEnabled,
    });

    AnalyticsService.eventGTM(EventNameGTM.ADDRESS_ADDED);

    if (result.ok && result.point) {
      setCurrentPoint({ ...result.point, isNew: true } as PointWithDevice);
      await handleSuccessCreatePointWithPayment();
    } else {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    }
  };

  const onPointCreateConfirmModalShow = () => {
    openModal(ModalTypes.POINT_CREATE_CONFIRM, {
      onSubmit: async () => {
        await handleCreateClick();
      },
      cost: subscriptionCostResult.data.extendCost,
    });
  };

  return {
    addressState,
    extendCost: subscriptionCostResult.data.extendCost,
    createPointError,
    userOnboardingFeaturesLoading,
    isButtonDisabled:
      !ValidationUtils.validateAddress(addressState.value) || isAcceptanceAdvancedPending || isAcceptanceAdvancedNull,
    isButtonLoading: canAddPointWithPayment ? isButtonWithCostLoading : isButtonLoading,
    isShowHintAboutPrice: currentCompany?.isBillingEnabled && !hasTrialLicenses,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull:
      isAcceptanceAdvancedNull || (selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvancedLoading),
    isAcceptanceAdvancedPending,
    canAddPointWithPayment,
    onCreatePointButtonClick: canAddPointWithPayment ? onPointCreateConfirmModalShow : handleCreateClick,
  };
};

export default usePointCreateViewModel;
